import React from 'react';

interface HostMetricsBarProps {
  cpuPercent: number;
  ramPercent: number;
  ramUsedGB?: number;
  ramTotalGB?: number;
  cpuTemp?: number;
  gpuTemp?: number;
  diskPercent?: number;
  diskUsedGB?: number;
  diskTotalGB?: number;
  powerWatts?: number;
}

interface MetricChipProps {
  label: string;
  value: string;
  color: string;
}

const MetricChip: React.FC<MetricChipProps> = ({ label, value, color }) => (
  <div style={{
    background: `${color}10`,
    border: `1px solid ${color}40`,
    borderRadius: '6px',
    padding: '0.4rem 0.6rem',
    minWidth: '60px',
  }}>
    <div style={{
      fontSize: '0.55rem',
      color: color,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '2px',
      fontWeight: 'bold',
    }}>
      {label}
    </div>
    <div style={{
      fontSize: '0.9rem',
      fontWeight: 'bold',
      color: color,
      fontFamily: 'monospace',
    }}>
      {value}
    </div>
  </div>
);

/**
 * Displays host hardware metrics as compact inline chips
 * Used in group headers to show host system status
 */
const HostMetricsBar: React.FC<HostMetricsBarProps> = ({
  cpuPercent,
  ramPercent,
  ramUsedGB,
  ramTotalGB,
  cpuTemp,
  gpuTemp,
  diskPercent,
  diskUsedGB,
  diskTotalGB,
  powerWatts,
}) => {
  // Determine temp color based on value
  const getTempColor = (temp: number) => {
    if (temp > 75) return '#ff4444';
    if (temp > 60) return '#ff9800';
    return '#ffa726';
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap',
    }}>
      {/* CPU Usage */}
      <MetricChip
        label="CPU"
        value={`${cpuPercent.toFixed(0)}%`}
        color="#00d4ff"
      />

      {/* RAM Usage */}
      <MetricChip
        label="RAM"
        value={ramUsedGB !== undefined && ramTotalGB !== undefined
          ? `${ramUsedGB.toFixed(1)}/${ramTotalGB.toFixed(0)}G`
          : `${ramPercent.toFixed(0)}%`
        }
        color="#8a2be2"
      />

      {/* CPU Temp */}
      {cpuTemp !== undefined && cpuTemp > 0 && (
        <MetricChip
          label="CPU°"
          value={`${cpuTemp.toFixed(0)}°C`}
          color={getTempColor(cpuTemp)}
        />
      )}

      {/* GPU Temp */}
      {gpuTemp !== undefined && gpuTemp > 0 && (
        <MetricChip
          label="GPU°"
          value={`${gpuTemp.toFixed(0)}°C`}
          color={getTempColor(gpuTemp)}
        />
      )}

      {/* Disk Usage */}
      {diskPercent !== undefined && (
        <MetricChip
          label="Disk"
          value={diskUsedGB !== undefined && diskTotalGB !== undefined
            ? `${diskUsedGB.toFixed(0)}/${diskTotalGB.toFixed(0)}G`
            : `${diskPercent.toFixed(0)}%`
          }
          color="#4caf50"
        />
      )}

      {/* Power */}
      {powerWatts !== undefined && powerWatts > 0 && (
        <MetricChip
          label="Power"
          value={`${powerWatts.toFixed(1)}W`}
          color="#ff9800"
        />
      )}
    </div>
  );
};

export default HostMetricsBar;
